import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { useSettingsQuery } from "@/api/queries/admin/SettingsQueries";
import { useUpdateSettingsMutation } from "@/api/mutations/admin/SettingsMutations";
import { toast } from "sonner";
import { Loader2, Mail, Plus, Trash2, Bell, Send, Webhook } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { trpc } from "@/lib/trpc";
import { NotificationSettings, notificationSettingsSchema } from "@shared/validators/admin/settings-validators";
import { DEFAULT_FLOWS, EVENT_LABELS } from "@shared/types/system-notifications";
import { useTranslation } from "react-i18next";

const NotificationSettingsSection = () => {
    const { t } = useTranslation();
    const { data: settingsData, isLoading: isLoadingSettings } = useSettingsQuery();
    const { mutate: updateSettings, isPending: isUpdating } = useUpdateSettingsMutation();
    const testEmailMutation = trpc.settings.testEmail.useMutation();
    const testWebhookMutation = trpc.settings.testWebhook.useMutation();
    const [emailRecipient, setEmailRecipient] = useState("");
    const [selectedFlowIndex, setSelectedFlowIndex] = useState<number | null>(null);
    const [testEmailAddress, setTestEmailAddress] = useState("");

    const form = useForm<NotificationSettings>({
        resolver: zodResolver(notificationSettingsSchema),
        defaultValues: {
            email_enabled: false,
            email_provider: "smtp",
            email_config: {
                smtp_host: "",
                smtp_port: 587,
                smtp_secure: false,
                smtp_user: "",
                smtp_password: "",
                resend_api_key: "",
                from_email: "",
                from_name: "",
            },
            webhook_enabled: false,
            webhook_config: {
                url: "",
                secret: "",
                headers: {},
            },
            flows: DEFAULT_FLOWS,
        },
    });

    useEffect(() => {
        if (settingsData?.notification_settings) {
            const notificationSettings = settingsData.notification_settings as any;
            form.reset({
                email_enabled: notificationSettings.email_enabled ?? false,
                email_provider: notificationSettings.email_provider ?? "smtp",
                email_config: {
                    smtp_host: notificationSettings.email_config?.smtp_host ?? "",
                    smtp_port: notificationSettings.email_config?.smtp_port ?? 587,
                    smtp_secure: notificationSettings.email_config?.smtp_secure ?? false,
                    smtp_user: notificationSettings.email_config?.smtp_user ?? "",
                    smtp_password: notificationSettings.email_config?.smtp_password ?? "",
                    resend_api_key: notificationSettings.email_config?.resend_api_key ?? "",
                    from_email: notificationSettings.email_config?.from_email ?? "",
                    from_name: notificationSettings.email_config?.from_name ?? "",
                },
                webhook_enabled: notificationSettings.webhook_enabled ?? false,
                webhook_config: {
                    url: notificationSettings.webhook_config?.url ?? "",
                    secret: notificationSettings.webhook_config?.secret ?? "",
                    headers: notificationSettings.webhook_config?.headers ?? {},
                },
                flows: notificationSettings.flows?.length > 0 ? notificationSettings.flows : DEFAULT_FLOWS,
            });
        }
    }, [settingsData, form]);

    const onSubmit = (data: NotificationSettings) => {
        updateSettings(
            {
                notification_settings: data,
            },
            {
                onSuccess: () => {
                    toast.success(t("settings.notificationSettings.messages.settingsUpdated"));
                },
                onError: () => {
                    toast.error(t("settings.notificationSettings.messages.settingsUpdateFailed"));
                },
            }
        );
    };

    const addEmailRecipient = (flowIndex: number) => {
        if (!emailRecipient || !emailRecipient.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            toast.error(t("settings.notificationSettings.messages.validEmailRequired"));
            return;
        }

        const flows = form.getValues("flows");
        const flow = flows[flowIndex];
        
        if (!flow.channels.email) {
            flow.channels.email = {
                enabled: true,
                recipients: [],
            };
        }

        if (flow.channels.email.recipients.includes(emailRecipient)) {
            toast.error(t("settings.notificationSettings.messages.emailAlreadyAdded"));
            return;
        }

        flow.channels.email.recipients.push(emailRecipient);
        form.setValue("flows", flows);
        setEmailRecipient("");
        toast.success(t("settings.notificationSettings.messages.emailRecipientAdded"));
    };

    const removeEmailRecipient = (flowIndex: number, email: string) => {
        const flows = form.getValues("flows");
        const flow = flows[flowIndex];
        
        if (flow.channels.email) {
            flow.channels.email.recipients = flow.channels.email.recipients.filter((e) => e !== email);
            form.setValue("flows", flows);
            toast.success(t("settings.notificationSettings.messages.emailRecipientRemoved"));
        }
    };

    const handleTestEmail = async () => {
        if (!testEmailAddress || !testEmailAddress.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
            toast.error(t("settings.notificationSettings.messages.validEmailRequired"));
            return;
        }

        testEmailMutation.mutate(
            { email: testEmailAddress },
            {
                onSuccess: () => {
                    toast.success(t("settings.notificationSettings.messages.testEmailSent", { email: testEmailAddress }));
                    setTestEmailAddress("");
                },
                onError: (error: any) => {
                    toast.error(error.message || t("settings.notificationSettings.messages.testEmailFailed"));
                },
            }
        );
    };

    const handleTestWebhook = async () => {
        testWebhookMutation.mutate(undefined, {
            onSuccess: () => {
                toast.success(t("settings.notificationSettings.messages.testWebhookSent"));
            },
            onError: (error: any) => {
                toast.error(error.message || t("settings.notificationSettings.messages.testWebhookFailed"));
            },
        });
    };

    if (isLoadingSettings) {
        return (
            <div className="flex items-center justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin" />
            </div>
        );
    }

    const emailEnabled = form.watch("email_enabled");
    const emailProvider = form.watch("email_provider");
    const flows = form.watch("flows");

    return (
        <div className="space-y-6">
            <div className="space-y-2">
                <h2 className="text-2xl font-bold">{t("settings.notificationSettings.title")}</h2>
                <p className="text-base text-muted-foreground">
                    {t("settings.notificationSettings.description")}
                </p>
            </div>

            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="flex flex-col min-h-[600px]">
                    <div className="flex-1 space-y-6">
                    {/* Email Configuration */}
                    <Card>
                        <CardHeader>
                            <div className="flex items-center gap-2">
                                <Mail className="h-5 w-5" />
                                <CardTitle>{t("settings.notificationSettings.emailConfiguration.title")}</CardTitle>
                            </div>
                            <CardDescription>
                                {t("settings.notificationSettings.emailConfiguration.description")}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <FormField
                                control={form.control}
                                name="email_enabled"
                                render={({ field }) => (
                                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                        <div className="space-y-0.5">
                                            <FormLabel className="text-base">{t("settings.notificationSettings.emailConfiguration.enableEmailNotifications")}</FormLabel>
                                            <FormDescription>
                                                {t("settings.notificationSettings.emailConfiguration.allowSendingNotifications")}
                                            </FormDescription>
                                        </div>
                                        <FormControl>
                                            <Switch checked={field.value} onCheckedChange={field.onChange} />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />

                            {emailEnabled && (
                                <>
                                    <Separator />
                                    <FormField
                                        control={form.control}
                                        name="email_provider"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>{t("settings.notificationSettings.emailConfiguration.emailProvider")}</FormLabel>
                                                <Select onValueChange={field.onChange} value={field.value}>
                                                    <FormControl>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder={t("settings.notificationSettings.emailConfiguration.selectEmailProvider")} />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        <SelectItem value="resend">Resend</SelectItem>
                                                        <SelectItem value="smtp">SMTP</SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                <FormDescription>{t("settings.notificationSettings.emailConfiguration.chooseEmailDelivery")}</FormDescription>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    {emailProvider === "resend" && (
                                        <>
                                            <FormField
                                                control={form.control}
                                                name="email_config.resend_api_key"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>{t("settings.notificationSettings.emailConfiguration.resendApiKey")}</FormLabel>
                                                        <FormControl>
                                                            <Input type="password" placeholder="re_..." {...field} />
                                                        </FormControl>
                                                        <FormDescription>
                                                            {t("settings.notificationSettings.emailConfiguration.getApiKeyFrom")}{" "}
                                                            <a
                                                                href="https://resend.com/api-keys"
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="text-primary hover:underline"
                                                            >
                                                                resend.com/api-keys
                                                            </a>
                                                        </FormDescription>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </>
                                    )}

                                    {emailProvider === "smtp" && (
                                        <>
                                            <div className="grid grid-cols-2 gap-4">
                                                <FormField
                                                    control={form.control}
                                                    name="email_config.smtp_host"
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormLabel>{t("settings.notificationSettings.emailConfiguration.smtpHost")}</FormLabel>
                                                            <FormControl>
                                                                <Input placeholder="smtp.example.com" {...field} />
                                                            </FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />
                                                <FormField
                                                    control={form.control}
                                                    name="email_config.smtp_port"
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormLabel>{t("settings.notificationSettings.emailConfiguration.smtpPort")}</FormLabel>
                                                            <FormControl>
                                                                <Input
                                                                    type="number"
                                                                    placeholder="587"
                                                                    {...field}
                                                                    onChange={(e) => field.onChange(parseInt(e.target.value))}
                                                                />
                                                            </FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />
                                            </div>

                                            <FormField
                                                control={form.control}
                                                name="email_config.smtp_secure"
                                                render={({ field }) => (
                                                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                                        <div className="space-y-0.5">
                                                            <FormLabel>{t("settings.notificationSettings.emailConfiguration.useTlsSsl")}</FormLabel>
                                                            <FormDescription>{t("settings.notificationSettings.emailConfiguration.enableSecureConnection")}</FormDescription>
                                                        </div>
                                                        <FormControl>
                                                            <Switch checked={field.value} onCheckedChange={field.onChange} />
                                                        </FormControl>
                                                    </FormItem>
                                                )}
                                            />

                                            <div className="grid grid-cols-2 gap-4">
                                                <FormField
                                                    control={form.control}
                                                    name="email_config.smtp_user"
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormLabel>{t("settings.notificationSettings.emailConfiguration.smtpUsername")}</FormLabel>
                                                            <FormControl>
                                                                <Input placeholder="username" {...field} />
                                                            </FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />
                                                <FormField
                                                    control={form.control}
                                                    name="email_config.smtp_password"
                                                    render={({ field }) => (
                                                        <FormItem>
                                                            <FormLabel>{t("settings.notificationSettings.emailConfiguration.smtpPassword")}</FormLabel>
                                                            <FormControl>
                                                                <Input type="password" placeholder="••••••••" {...field} />
                                                            </FormControl>
                                                            <FormMessage />
                                                        </FormItem>
                                                    )}
                                                />
                                            </div>
                                        </>
                                    )}

                                    <Separator />

                                    <div className="grid grid-cols-2 gap-4">
                                        <FormField
                                            control={form.control}
                                            name="email_config.from_email"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t("settings.notificationSettings.emailConfiguration.fromEmail")}</FormLabel>
                                                    <FormControl>
                                                        <Input placeholder="notifications@example.com" {...field} />
                                                    </FormControl>
                                                    <FormDescription>{t("settings.notificationSettings.emailConfiguration.senderEmailAddress")}</FormDescription>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                        <FormField
                                            control={form.control}
                                            name="email_config.from_name"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>{t("settings.notificationSettings.emailConfiguration.fromName")}</FormLabel>
                                                    <FormControl>
                                                        <Input placeholder="Junet Notifications" {...field} />
                                                    </FormControl>
                                                    <FormDescription>{t("settings.notificationSettings.emailConfiguration.senderDisplayName")}</FormDescription>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    </div>
                                </>
                            )}
                            
                            {emailEnabled && (
                                <>
                                    <Separator />
                                    
                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2">
                                            <Send className="h-4 w-4 text-muted-foreground" />
                                            <span className="text-sm font-medium">{t("settings.notificationSettings.emailConfiguration.testEmailConfiguration")}</span>
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                            {t("settings.notificationSettings.emailConfiguration.sendTestEmailDescription")}
                                        </p>
                                        <div className="flex gap-2">
                                            <Input
                                                placeholder="test@example.com"
                                                value={testEmailAddress}
                                                onChange={(e) => setTestEmailAddress(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") {
                                                        e.preventDefault();
                                                        handleTestEmail();
                                                    }
                                                }}
                                            />
                                            <Button
                                                type="button"
                                                variant="outline"
                                                onClick={handleTestEmail}
                                                disabled={testEmailMutation.isPending || !testEmailAddress}
                                            >
                                                {testEmailMutation.isPending ? (
                                                    <>
                                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                        {t("settings.notificationSettings.emailConfiguration.sending")}
                                                    </>
                                                ) : (
                                                    <>
                                                        <Send className="mr-2 h-4 w-4" />
                                                        {t("settings.notificationSettings.emailConfiguration.sendTest")}
                                                    </>
                                                )}
                                            </Button>
                                        </div>
                                    </div>
                                </>
                            )}
                        </CardContent>
                    </Card>

                    {/* Webhook Configuration */}
                    <Card>
                        <CardHeader>
                            <div className="flex items-center gap-2">
                                <Webhook className="h-5 w-5" />
                                <CardTitle>{t("settings.notificationSettings.webhookConfiguration.title")}</CardTitle>
                            </div>
                            <CardDescription>
                                {t("settings.notificationSettings.webhookConfiguration.description")}
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <FormField
                                control={form.control}
                                name="webhook_enabled"
                                render={({ field }) => (
                                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                                        <div className="space-y-0.5">
                                            <FormLabel className="text-base">{t("settings.notificationSettings.webhookConfiguration.enableWebhookNotifications")}</FormLabel>
                                            <FormDescription>
                                                {t("settings.notificationSettings.webhookConfiguration.allowSendingWebhooks")}
                                            </FormDescription>
                                        </div>
                                        <FormControl>
                                            <Switch checked={field.value} onCheckedChange={field.onChange} />
                                        </FormControl>
                                    </FormItem>
                                )}
                            />

                            {form.watch("webhook_enabled") && (
                                <>
                                    <Separator />
                                    <FormField
                                        control={form.control}
                                        name="webhook_config.url"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>{t("settings.notificationSettings.webhookConfiguration.webhookUrl")}</FormLabel>
                                                <FormControl>
                                                    <Input placeholder="https://your-server.com/webhooks/notifications" {...field} />
                                                </FormControl>
                                                <FormDescription>{t("settings.notificationSettings.webhookConfiguration.httpsEndpoint")}</FormDescription>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="webhook_config.secret"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>{t("settings.notificationSettings.webhookConfiguration.webhookSecret")}</FormLabel>
                                                <FormControl>
                                                    <Input 
                                                        type="password" 
                                                        placeholder="your-secret-key" 
                                                        {...field} 
                                                    />
                                                </FormControl>
                                                <FormDescription>
                                                    {t("settings.notificationSettings.webhookConfiguration.hmacSecret")}
                                                </FormDescription>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <Separator />

                                    <div className="space-y-3">
                                        <div className="flex items-center gap-2">
                                            <Send className="h-4 w-4 text-muted-foreground" />
                                            <span className="text-sm font-medium">{t("settings.notificationSettings.webhookConfiguration.testWebhookConfiguration")}</span>
                                        </div>
                                        <p className="text-sm text-muted-foreground">
                                            {t("settings.notificationSettings.webhookConfiguration.sendTestWebhookDescription")}
                                        </p>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            onClick={handleTestWebhook}
                                            disabled={testWebhookMutation.isPending || !form.watch("webhook_config.url")}
                                        >
                                            {testWebhookMutation.isPending ? (
                                                <>
                                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                    {t("settings.notificationSettings.emailConfiguration.sending")}
                                                </>
                                            ) : (
                                                <>
                                                    <Send className="mr-2 h-4 w-4" />
                                                    {t("settings.notificationSettings.webhookConfiguration.sendTestWebhook")}
                                                </>
                                            )}
                                        </Button>
                                    </div>
                                </>
                            )}
                        </CardContent>
                    </Card>

                    {/* Notification Flows */}
                    <Card>
                        <CardHeader>
                            <div className="flex items-center gap-2">
                                <Bell className="h-5 w-5" />
                                <CardTitle>{t("settings.notificationSettings.notificationFlows.title")}</CardTitle>
                            </div>
                            <CardDescription>{t("settings.notificationSettings.notificationFlows.description")}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {flows.map((flow, index) => {
                                const eventInfo = EVENT_LABELS[flow.event];
                                return (
                                    <div key={flow.id} className="rounded-lg border p-4 space-y-4">
                                        <div className="flex items-start justify-between">
                                            <div className="space-y-1">
                                                <div className="flex items-center gap-2">
                                                    <h4 className="font-semibold">{flow.name}</h4>
                                                    <Badge variant="secondary">{eventInfo.label}</Badge>
                                                </div>
                                                <p className="text-sm text-muted-foreground">{eventInfo.description}</p>
                                            </div>
                                            <FormField
                                                control={form.control}
                                                name={`flows.${index}.enabled`}
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormControl>
                                                            <Switch checked={field.value} onCheckedChange={field.onChange} />
                                                        </FormControl>
                                                    </FormItem>
                                                )}
                                            />
                                        </div>

                                        {flow.enabled && (
                                            <>
                                                <Separator />
                                                <div className="space-y-3">
                                                    <div className="flex items-center gap-2">
                                                        <Mail className="h-4 w-4 text-muted-foreground" />
                                                        <span className="text-sm font-medium">{t("settings.notificationSettings.notificationFlows.emailRecipients")}</span>
                                                    </div>
                                                    
                                                    <div className="flex flex-wrap gap-2">
                                                        {flow.channels.email?.recipients.map((email) => (
                                                            <Badge key={email} variant="outline" className="gap-1">
                                                                {email}
                                                                <button
                                                                    type="button"
                                                                    onClick={() => removeEmailRecipient(index, email)}
                                                                    className="ml-1 hover:text-destructive"
                                                                >
                                                                    <Trash2 className="h-3 w-3" />
                                                                </button>
                                                            </Badge>
                                                        ))}
                                                    </div>

                                                    <div className="flex gap-2">
                                                        <Input
                                                            placeholder="email@example.com"
                                                            value={selectedFlowIndex === index ? emailRecipient : ""}
                                                            onChange={(e) => {
                                                                setSelectedFlowIndex(index);
                                                                setEmailRecipient(e.target.value);
                                                            }}
                                                            onKeyDown={(e) => {
                                                                if (e.key === "Enter") {
                                                                    e.preventDefault();
                                                                    setSelectedFlowIndex(index);
                                                                    addEmailRecipient(index);
                                                                }
                                                            }}
                                                        />
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="icon"
                                                            onClick={() => {
                                                                setSelectedFlowIndex(index);
                                                                addEmailRecipient(index);
                                                            }}
                                                        >
                                                            <Plus className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </div>

                                                {/* Webhook Channel Toggle */}
                                                <Separator />
                                                <div className="space-y-3">
                                                    <div className="flex items-center justify-between">
                                                        <div className="flex items-center gap-2">
                                                            <Webhook className="h-4 w-4 text-muted-foreground" />
                                                            <span className="text-sm font-medium">{t("settings.notificationSettings.notificationFlows.webhookNotifications")}</span>
                                                        </div>
                                                        <FormField
                                                            control={form.control}
                                                            name={`flows.${index}.channels.webhook.enabled`}
                                                            render={({ field }) => (
                                                                <FormItem className="flex items-center gap-2 space-y-0">
                                                                    <FormControl>
                                                                        <Switch 
                                                                            checked={field.value ?? false} 
                                                                            onCheckedChange={field.onChange}
                                                                            disabled={!form.watch("webhook_enabled")}
                                                                        />
                                                                    </FormControl>
                                                                </FormItem>
                                                            )}
                                                        />
                                                    </div>
                                                    {!form.watch("webhook_enabled") && (
                                                        <p className="text-xs text-muted-foreground">
                                                            {t("settings.notificationSettings.notificationFlows.enableWebhookConfig")}
                                                        </p>
                                                    )}
                                                    {form.watch("webhook_enabled") && flow.channels.webhook?.enabled && (
                                                        <p className="text-xs text-muted-foreground">
                                                            {t("settings.notificationSettings.notificationFlows.notificationsSentTo")} {form.watch("webhook_config.url") || t("settings.notificationSettings.notificationFlows.noUrlConfigured")}
                                                        </p>
                                                    )}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                );
                            })}
                        </CardContent>
                        <div className="border-t p-6">
                            <Button type="submit" disabled={isUpdating}>
                                {isUpdating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {isUpdating ? t("common.loading") : t("common.save")}
                            </Button>
                        </div>
                    </Card>
                    </div>
                </div>
                </form>
            </Form>
        </div>
    );
};

export default NotificationSettingsSection;